import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getAllPatients } from "../../../api/apiService";
import Search from "../../../assets/images/comman/search-outline.svg";
import BillingIcon from "../../../assets/images/comman/sidebar/Billing.svg";
import LinkAbhaButton from "../Patients/LinkAbhaButton";
import NotifyAbhaButton from "../Patients/NotifyAbhaButton";
import { getAgoTime } from "../Patients/utils";
import {
  ensurePatientHasVisit,
  patientHasNoVisits,
  calculateAge,
  formatPatientName,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import CreateVisitModal from "../../../components/common/CreateVisitModal";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";

const BillingPatientList = ({ onSelectPatient }) => {
  const { can } = useAuth();
  const { toasts, removeToast, showError } = useToast();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPatients, setTotalPatients] = useState(0);
  const [navigatingPatientId, setNavigatingPatientId] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [patientForVisit, setPatientForVisit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  const getTimeAgo = (dateString) => getAgoTime(dateString);

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllPatients();

      if (response.success) {
        const transformedPatients = (response.data?.patients || []).map((patient) => {
          const rawName =
            patient.name && typeof patient.name === "string" && patient.name.trim() && patient.name.trim() !== "undefined undefined"
              ? patient.name.trim()
              : patient.fullName && typeof patient.fullName === "string" && patient.fullName.trim() && patient.fullName.trim() !== "undefined undefined"
              ? patient.fullName.trim()
              : formatPatientName(patient) || "Patient";
          const fullName = rawName;

          const dobFormatted = formatDobDisplay(patient.dob);

          let computedAge = "";
          if (dobFormatted) {
            const calc = calculateAge(dobFormatted);
            if (calc && typeof calc === "number" && !isNaN(calc) && calc > 0 && calc < 130) {
              computedAge = calc;
            }
          }
          if (!computedAge && patient.age !== undefined && patient.age !== null && patient.age !== "") {
            const parsed = parseInt(String(patient.age).replace(/[^0-9]/g, ""), 10);
            if (!isNaN(parsed) && parsed > 0 && parsed < 130) {
              computedAge = parsed;
            }
          }

          const visitTimestamp =
            patient.lastVisitDate ||
            patient.time ||
            patient.appointmentDate ||
            patient.updatedAt ||
            patient.lastVisit ||
            patient.createdAt;

          const lastVisit = formatDate(visitTimestamp);
          const lastVisitAgo = visitTimestamp ? getTimeAgo(visitTimestamp) : "";

          const safeGet = (value) => value || "";

          const isAbhaLinked = Boolean(
            patient.isAbhaLinked === true ||
            patient.isabhalinked === true ||
            patient.isABHALinked === true ||
            String(patient.isAbhaLinked).toLowerCase() === "true" ||
            String(patient.isabhalinked).toLowerCase() === "true" ||
            String(patient.isABHALinked).toLowerCase() === "true" ||
            (patient.maskedAbha && String(patient.maskedAbha).trim() && patient.maskedAbha !== "N/A") ||
            (patient.ABHANumber && String(patient.ABHANumber).trim() && patient.ABHANumber !== "N/A") ||
            (patient.abhaNumber && String(patient.abhaNumber).trim() && patient.abhaNumber !== "N/A") ||
            (patient.abhaaddress && String(patient.abhaaddress).trim()) ||
            (patient.abhaAddress && String(patient.abhaAddress).trim()) ||
            (patient.ABHAAddress && String(patient.ABHAAddress).trim())
          );

          const maskedAbha =
            patient.maskedAbha ||
            (patient.ABHANumber ? `**** ${String(patient.ABHANumber).slice(-4)}` : "") ||
            patient.formattedAbhaId ||
            (patient.abhaaddress || patient.abhaAddress || patient.ABHAAddress || "");

          return {
            id: patient._id || patient.id,
            _id: patient._id || patient.id,
            name: fullName,
            fullName: fullName,
            age: computedAge ? `${computedAge}yr` : "",
            calculatedAge: computedAge,
            dob: dobFormatted,
            gender: safeGet(patient.gender),
            isAbhaLinked,
            isabhalinked: isAbhaLinked,
            maskedAbha,
            abhaId: maskedAbha || (isAbhaLinked ? "Linked" : "N/A"),
            formattedAbhaId: maskedAbha || patient.ABHANumber || "",
            lastVisit,
            lastVisitAgo,
            opdId:
              safeGet(patient.opdId) || (patient._id || patient.id || "").slice(-8).toUpperCase(),
            queue:
              patient.queue !== undefined && patient.queue !== null
                ? String(patient.queue).padStart(2, "0")
                : "—",
            uhid:
              safeGet(patient.uhid || patient.UHID) ||
              (patient._id || patient.id || "").slice(-8).toUpperCase(),
            fee:
              safeGet(
                patient.fee !== undefined && patient.fee !== ""
                  ? (typeof patient.fee === "number" ? `₹${patient.fee}` : patient.fee)
                  : patient.consultationFee
                  ? `₹${patient.consultationFee}`
                  : "",
              ) || "",
            time: safeGet(patient.time || patient.appointmentTime || "") || "",
            date: formatDate(patient.lastVisitDate || patient.appointmentDate || patient.time || new Date()),
            doctor:
              safeGet(
                patient.doctor ||
                patient.consultingDoctor ||
                patient.assignedDoctor ||
                patient.lastVisitedDoctor ||
                patient?.visits?.[0]?.doctor,
              ) || "",
            mobile: safeGet(patient.mobile),
            address: safeGet(patient.address),
            pincode: safeGet(patient.pincode),
            status: safeGet(patient.status),
            visitType: patient.visitType || "General OPD",
            rawData: {
              ...patient,
              name: fullName,
              fullName: fullName,
              calculatedAge: computedAge,
              age: computedAge ? `${computedAge}yr` : "",
              isAbhaLinked,
              isabhalinked: isAbhaLinked,
              maskedAbha,
              formattedAbhaId: maskedAbha || patient.ABHANumber || "",
              ABHANumber: safeGet(patient.ABHANumber),
              ABHAAddress: safeGet(
                patient.ABHAAddress ||
                  patient.abhaAddress ||
                  patient.abhaaddress,
              ),
              yearOfBirth:
                patient.yearOfBirth ||
                (patient.dob ? new Date(patient.dob).getFullYear() : null),
              gender: safeGet(patient.gender),
              dateOfBirth: safeGet(patient.dob),
              visitId: safeGet(
                patient.visitId ||
                  patient.visits?.[0]?.visitId ||
                  patient.visits?.[0]?._id,
              ),
              visitType: patient.visitType || "General OPD",
            },
          };
        });

        setPatients(transformedPatients);
        setTotalPatients(response.data?.total || transformedPatients.length);
      } else {
        setError("Failed to fetch patients data");
      }
    } catch (err) {
      console.error("Error fetching patients for billing:", err);
      setError(err.message || "Failed to fetch patients data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handlePatientSelect = async (patient) => {
    if (navigatingPatientId) return;

    if (patientHasNoVisits(patient)) {
      setPatientForVisit(patient);
      setShowVisitModal(true);
      return;
    }

    try {
      setNavigatingPatientId(patient.id ?? patient._id);
      const patientToUse = await ensurePatientHasVisit(patient);
      const effectivePatientId = patientToUse.id || patient.id || patient._id;
      if (onSelectPatient) {
        onSelectPatient(patientToUse.rawData, effectivePatientId);
      }
    } catch (err) {
      console.error("Failed to verify patient visit for billing:", err);
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to verify patient visit. Please try again.",
      );
    } finally {
      setNavigatingPatientId(null);
    }
  };

  const handleVisitCreated = (result) => {
    const apiPatient = result.patientData ?? result;
    const existingRaw = patientForVisit?.rawData ?? patientForVisit;

    const mergedRawData = {
      ...existingRaw,
      ...apiPatient,
      visits: apiPatient.visits ?? existingRaw?.visits,
      visitId:
        apiPatient.visitId ??
        apiPatient.visits?.[0]?.visitId ??
        existingRaw?.visitId,
    };

    const effectivePatientId = mergedRawData._id || mergedRawData.id;
    setShowVisitModal(false);
    setPatientForVisit(null);
    if (onSelectPatient) {
      onSelectPatient(mergedRawData, effectivePatientId);
    }
  };

  const filteredPatients = patients.filter((patient) => {
    if (activeTab === "OPD" && patient.visitType !== "General OPD") return false;
    if (activeTab === "IPD" && patient.visitType !== "IPD") return false;
    if (activeTab === "Emergency" && patient.visitType !== "Emergency") return false;

    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      patient.name?.toLowerCase().includes(q) ||
      patient.mobile?.toLowerCase().includes(q) ||
      patient.uhid?.toLowerCase().includes(q) ||
      patient.opdId?.toLowerCase().includes(q) ||
      patient.doctor?.toLowerCase().includes(q)
    );
  });

  return (
    <>
      <style>
        {`
          .patient-row-hover:hover {
            background-color: #f8f9fa !important;
            transition: background-color 0.2s ease;
          }
          .patient-row-hover:hover td {
            background-color: inherit !important;
          }
        `}
      </style>
      <div className="container-fluid">
        <div className="opd-page">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h3 className="mb-1" style={{ fontWeight: 600, color: "#0B1B8D" }}>
                Billing
              </h3>
              <p className="text-muted small mb-0">
                Select a patient from the list below to create, edit, or view invoices.
              </p>
            </div>
            {can(PERMISSIONS.PATIENTS_REGISTER) && (
              <Link to="/add-patient" className="btn btn-primary btn-sm d-flex align-items-center gap-1">
                <i className="material-icons" style={{ fontSize: "18px" }}>add</i>
                <span>Add Patient</span>
              </Link>
            )}
          </div>

          <div className="header-section-new">
            <div className="header-left">
              <ul className="nav nav-tabs-new">
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "All" ? "active" : ""}`}
                    onClick={() => setActiveTab("All")}
                    style={{ cursor: "pointer" }}
                  >
                    All Patients
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "OPD" ? "active" : ""}`}
                    onClick={() => setActiveTab("OPD")}
                    style={{ cursor: "pointer" }}
                  >
                    OPD
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "IPD" ? "active" : ""}`}
                    onClick={() => setActiveTab("IPD")}
                    style={{ cursor: "pointer" }}
                  >
                    IPD
                  </span>
                </li>
                <li className="nav-item">
                  <span
                    className={`nav-link ${activeTab === "Emergency" ? "active" : ""}`}
                    onClick={() => setActiveTab("Emergency")}
                    style={{ cursor: "pointer" }}
                  >
                    Emergency Cases
                  </span>
                </li>
              </ul>
            </div>
            <div className="header-right">
              <div className="search-bar-new">
                <span className="search-icon">
                  <img src={Search} alt="Search" />
                </span>
                <input
                  type="text"
                  placeholder="Search by Name, Mob, ID, UHID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className="btn-refresh-new"
                onClick={fetchPatients}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "↻ Refresh"}
              </button>
            </div>
          </div>

          <div className="OpdBodySection">
            <div className="tab-content">
              <div id="All" className="tab-pane active">
                <br />
                <div className="opdAllTable">
                  <table className="table table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Patient Details</th>
                        <th>OPD ID</th>
                        <th>Queue</th>
                        <th>ABHA ID</th>
                        <th>UHID</th>
                        <th>Doctor</th>
                        <th>Last Visit</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2 mb-0">Loading patients...</p>
                          </td>
                        </tr>
                      ) : error ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <div className="alert alert-danger mb-0">
                              <strong>Error:</strong> {error}
                              <br />
                              <button
                                className="btn btn-sm btn-outline-primary mt-2"
                                onClick={fetchPatients}
                              >
                                Retry
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : filteredPatients.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-5">
                            <div className="alert alert-info mb-0">
                              <strong>No patients found.</strong>
                              {searchTerm && <p className="mb-0 mt-1">No patients match "{searchTerm}". Try clearing your search.</p>}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredPatients.map((patient, index) => (
                          <tr
                            key={patient.id || index}
                            style={{
                              cursor: navigatingPatientId ? "wait" : "pointer",
                              opacity: navigatingPatientId === (patient.id ?? patient._id) ? 0.7 : 1,
                            }}
                            onClick={() => handlePatientSelect(patient)}
                            title="Click to bill this patient"
                            className="patient-row-hover"
                          >
                            <td>
                              <div className="patientDetails">
                                <h5>{patient.name}</h5>
                                <p>
                                  {patient.age ? `Age: ${patient.age}` : ""}
                                  {patient.dob ? `${patient.age ? " | " : ""}DOB: ${patient.dob}` : ""}
                                  {patient.gender ? ` | ${patient.gender}` : ""}
                                </p>
                                {patient.lastVisit ? (
                                  <p>
                                    Last Visit: {patient.lastVisit}
                                    {patient.lastVisitAgo ? ` (${patient.lastVisitAgo})` : ""}
                                  </p>
                                ) : null}
                                {patient.mobile && (
                                  <p className="text-muted small">
                                    Mobile: {patient.mobile}
                                  </p>
                                )}
                                {navigatingPatientId === (patient.id ?? patient._id) && (
                                  <p className="text-primary small mb-0 mt-1">
                                    <span
                                      className="spinner-border spinner-border-sm me-1"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Opening billing…
                                  </p>
                                )}
                              </div>
                            </td>
                            <td>
                              <h5>{patient.opdId}</h5>
                            </td>
                            <td>
                              <h5>{patient.queue.toString().padStart(2, "0")}</h5>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              {patient.isAbhaLinked || (patient.abhaId && patient.abhaId !== "N/A") ? (
                                <div>
                                  <h5 style={{ fontFamily: "monospace", margin: 0 }}>
                                    {patient.maskedAbha || patient.abhaId || "Linked"}
                                  </h5>
                                  {patient.ABHAAddress && (
                                    <small className="text-muted">
                                      {patient.ABHAAddress}
                                    </small>
                                  )}
                                </div>
                              ) : (
                                <div className="d-flex flex-column gap-1">
                                  <LinkAbhaButton patient={patient} />
                                  <NotifyAbhaButton patientId={patient.id} />
                                </div>
                              )}
                            </td>
                            <td>
                              <h5>{patient.uhid}</h5>
                            </td>
                            <td>
                              <h5>{patient.doctor || "Not Assigned"}</h5>
                            </td>
                            <td>
                              <div className="patientTime">
                                <h5>{patient.time || ""}</h5>
                                <p>{patient.date}</p>
                              </div>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePatientSelect(patient);
                                }}
                                disabled={navigatingPatientId === (patient.id ?? patient._id)}
                              >
                                <img
                                  src={BillingIcon}
                                  alt="Billing"
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    filter: "brightness(0) invert(1)",
                                  }}
                                />
                                <span>Bill Patient</span>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {!loading && !error && filteredPatients.length > 0 && (
                    <div className="mt-3 text-muted">
                      <small>Total Patients: {totalPatients}</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateVisitModal
        isOpen={showVisitModal}
        patient={patientForVisit}
        onClose={() => {
          setShowVisitModal(false);
          setPatientForVisit(null);
        }}
        onSuccess={handleVisitCreated}
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default BillingPatientList;
